/**
 * UltamateRewardsOffers is consist of LargedealRail and manages different states
 *
 * @module views/components/UltamateRewardsOffers
 * @memberof -Common
 */

import './UltamateRewardsOffers.scss';

import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { constants } from '@ulta/utils/constants/constants';

import BonusOffersRail from '../../components/BonusOffersRail/BonusOffersRail';
import DealsRail from '../../components/DealsRail/DealsRail';

/**
   * Represents a UltamateRewardsOffers component
   *
   * @method
   * @param {UltamateRewardsOffersProps} props - React properties passed from composition
   * @returns UltamateRewardsOffers
   */
export const UltamateRewardsOffers = function( props ){
  const {
    offers, variantType, titleText = defaultProps.titleText, title, setShowSpacer
  } = props;

  useEffect( ()=>{
    if( setShowSpacer ){
      setShowSpacer( hasItems( offers ) );
    }
  }, [offers] );

  if( !hasItems( offers ) ){
    return null;
  }

  // temporary title object for backwards compatibility
  // if titleText is not used, fallback to title string
  const headerTitle = {
    text: titleText?.text || title,
    htmlTag: titleText?.htmlTag || defaultProps.titleText.htmlTag
  };

  const updatedProps = Object.assign( { headerTitle }, props );

  return (
    <div className='UltamateRewardsOffers'>
      { variantType === ULTAMATE_REWARDS_RAIL_VIEW_OPTION?.DEALS ? (
        <DealsRail { ...spreadProps( { ...updatedProps } ) } />
      ) : (
        <BonusOffersRail { ...spreadProps( { ...updatedProps } ) } />
      ) }
    </div>
  );
};

/**
 * @const {string} ULTAMATE_REWARDS_RAIL_VIEW_OPTION - option for horizontal rail view
 */
export const ULTAMATE_REWARDS_RAIL_VIEW_OPTION = {
  DEALS: 'mediumDealCard'
  // NOTE: beware backwards compatibility issues with the OFFERS variantType. DXL only sends this variantType prop for our new DEALS variant type.
};

/**
   * Default values for passed properties
   * @type {object}
   */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  titleText: {
    htmlTag: 'h2'
  }
};

/**
   * Property type definitions
   * @typedef UltamateRewardsOffersProps
   * @type {object}
   * @property {Array<object>} offers - An array of offer objects to be displayed.
   * @property {string} [variantType] - Determines which rail component to render. Can be 'mediumDealCard' for `DealsRail`.
   * @property {object} [titleText] - An object for the title, containing `text` and `htmlTag`.
   * @property {string} [title] - A fallback string for the title if `titleText.text` is not provided.
   * @property {Function} [setShowSpacer] - A callback function to control the visibility of a spacer element based on whether offers exist.
   * @property {Function} [invokeMutation] - A function passed down to handle offer activation.
   * @property {object} [viewAllOffersAction] - An action object for the "View All" link.
   * @property {Function} [nextClickAction] - A function to handle the "next" slide action.
   * @property {Function} [previousClickAction] - A function to handle the "previous" slide action.
   * @property {object} [_railADAmeta] - Accessibility metadata for the underlying rail.
   * @property {object} [_railMeta] - General metadata for the underlying rail.
  */

export const propTypes = {
  offers: PropTypes.array,
  variantType: PropTypes.string,
  titleText: PropTypes.object,
  title: PropTypes.string,
  setShowSpacer: PropTypes.func,
  invokeMutation: PropTypes.func,
  viewAllOffersAction: PropTypes.object,
  nextClickAction: PropTypes.func,
  previousClickAction: PropTypes.func,
  _railADAmeta: PropTypes.object,
  _railMeta: PropTypes.object
};

UltamateRewardsOffers.propTypes = propTypes;
UltamateRewardsOffers.defaultProps = defaultProps;

export default UltamateRewardsOffers;